import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SlidersHorizontal, X, Gem, ShieldCheck, Diamond, IndianRupee, PackageCheck,
  ChevronRight, LayoutGrid, List, ArrowUpDown,
} from "lucide-react";
import { StoreLayout } from "@/components/StoreLayout";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetFooter, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from "@/components/ui/pagination";
import { formatINR, api } from "@/lib/api";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const PAGE_SIZE = 12;

const TYPE_FILTERS = {
  Earrings: ["Studs", "Drops", "Hoops", "Jhumkas", "Solitaires"],
  Rings: ["Solitaire", "Eternity", "Bypass", "Halo"],
  Pendants: ["Solitaire", "Halo", "Cluster"],
  Bracelets: ["Tennis", "Bangle", "Chain"],
};

// Empty, JSON-shape-matching default so the very first render (before /api/search/analyze
// resolves) never has to null-check every field individually below.
const EMPTY_CONCEPTS = {
  category: [], karat: "", gold_color: [], diamond_type: "", diamond_shape: [],
  gemstone: [], style: [], occasion: [], metal: [], jewellery_type_term: [], leftover_terms: [],
};

/* One removable pill: label + × button. Reused for both the manual sidebar-filter chips
   (category/karat/gemstone/in-stock/price) and the Smart-Search-derived concept chips
   (style/occasion/jewellery-type/etc.) so both sources render identically. */
function FilterChip({ testid, label, onRemove }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 shrink-0 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--gold-light))] pl-3 pr-1.5 py-1 text-xs font-medium text-[hsl(var(--primary))] whitespace-nowrap"
    >
      {label}
      <button
        type="button"
        data-testid={`${testid}-remove`}
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-[hsl(var(--primary))]/15 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))] transition-colors duration-150"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/* A single expandable filter row: icon + label on the left, current value + chevron on the right */
function FilterRow({ value: valueId, icon: Icon, label, current, children }) {
  return (
    <AccordionItem value={valueId} className="border-b border-border">
      <AccordionTrigger className="py-4 hover:no-underline [&>svg]:hidden">
        <div className="flex items-center justify-between w-full pr-1">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 text-[hsl(var(--primary))]" strokeWidth={1.5} />
            <span className="text-[15px] font-medium text-foreground">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground max-w-[140px] truncate">{current}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4 pt-1">{children}</AccordionContent>
    </AccordionItem>
  );
}

function OptionPills({ options, value, onChange, allLabel = "All" }) {
  const all = [{ v: "all", l: allLabel }, ...options.map((o) => ({ v: o, l: o }))];
  return (
    <div className="flex flex-wrap gap-2">
      {all.map(({ v, l }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-3.5 py-1.5 rounded-full text-sm transition-colors duration-150 border ${
            value === v
              ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]"
              : "bg-card text-foreground/70 border-border hover:border-[hsl(var(--primary))]"
          }`}
        >{l}</button>
      ))}
    </div>
  );
}

function Filters({ facets, categories, filters, setFilters, priceRange, setPriceRange }) {
  return (
    <Accordion type="single" collapsible className="w-full border-t border-border">
      <FilterRow value="category" icon={Gem} label="Category" current={filters.category === "all" ? "All" : filters.category}>
        <OptionPills options={categories.map((c) => c.name)} value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} allLabel="All Categories" />
      </FilterRow>
      <FilterRow value="karat" icon={ShieldCheck} label="Metal Purity" current={filters.karat === "all" ? "All" : filters.karat}>
        <OptionPills options={facets.karats || []} value={filters.karat} onChange={(v) => setFilters({ ...filters, karat: v })} allLabel="All Karats" />
      </FilterRow>
      <FilterRow value="gemstone" icon={Diamond} label="Gemstone" current={filters.gemstone === "all" ? "All" : filters.gemstone}>
        <OptionPills options={facets.gemstones || []} value={filters.gemstone} onChange={(v) => setFilters({ ...filters, gemstone: v })} allLabel="All Gemstones" />
      </FilterRow>
      <FilterRow value="price" icon={IndianRupee} label="Price Range" current={`${formatINR(priceRange[0])} – ${formatINR(priceRange[1])}`}>
        <div className="px-1 pt-2">
          <Slider data-testid="filter-price-slider" min={facets.min_price || 0} max={facets.max_price || 1000000} step={1000}
            value={priceRange} onValueChange={setPriceRange} />
          <div className="flex justify-between text-xs text-muted-foreground mt-3 font-mono">
            <span>{formatINR(priceRange[0])}</span><span>{formatINR(priceRange[1])}</span>
          </div>
        </div>
      </FilterRow>
      <FilterRow value="stock" icon={PackageCheck} label="Availability" current={filters.in_stock ? "In stock" : "All"}>
        <div className="flex items-center justify-between px-1">
          <Label htmlFor="instock" className="text-sm text-muted-foreground">Hide out-of-stock pieces</Label>
          <Switch id="instock" data-testid="filter-instock" checked={filters.in_stock} onCheckedChange={(v) => setFilters({ ...filters, in_stock: v })} />
        </div>
      </FilterRow>
    </Accordion>
  );
}

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState({});
  // `loading` = very first fetch only (shows the skeleton). `refreshing` = every fetch after
  // that (category/filter/sort changes) — the current products stay on screen while it runs,
  // instead of blanking back to the skeleton, so switching filters never looks like a fresh load.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [sort, setSort] = useState("newest");
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [typeFilter, setTypeFilter] = useState("All");
  const [view, setView] = useState("grid");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "all",
    karat: "all", gemstone: "all", in_stock: false,
  });
  const search = searchParams.get("search") || "";
  const featuredOnly = searchParams.get("featured") === "true";
  const [searchConcepts, setSearchConcepts] = useState(EMPTY_CONCEPTS);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/facets").then(({ data }) => { setFacets(data); setPriceRange([data.min_price || 0, data.max_price || 1000000]); });
  }, []);

  // Whenever the free-text search changes, ask the Smart Search parser what it recognised
  // (category/karat/gold colour/diamond type & shape/gemstone/style/occasion/jewellery-type)
  // so those concepts can be shown as individually-removable chips next to the search box —
  // rather than the search staying an opaque string the shopper can only clear entirely.
  useEffect(() => {
    if (!search) { setSearchConcepts(EMPTY_CONCEPTS); return; }
    let cancelled = false;
    api.get("/search/analyze", { params: { q: search } }).then(({ data }) => {
      if (!cancelled) setSearchConcepts({ ...EMPTY_CONCEPTS, ...data });
    }).catch(() => { if (!cancelled) setSearchConcepts(EMPTY_CONCEPTS); });
    return () => { cancelled = true; };
  }, [search]);

  // Scroll back to the top whenever the page changes — otherwise navigating from page 1 to 2
  // (or 2 to 3) opens the new page at whatever scroll position the user was already at.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    // Guard against creating a brand-new (but value-identical) `filters` object here —
    // that would recreate `fetchProducts` and refire the fetch effect for NO real reason,
    // stacking on top of the actual mount-time fetch and reading like a "double load" flicker.
    // Only produce a new object (and thus only refetch) when the category actually changed.
    const cat = searchParams.get("category") || "all";
    setFilters((f) => (f.category === cat ? f : { ...f, category: cat }));
    setTypeFilter("All");
  }, [searchParams]);

  const fetchProducts = useCallback(async () => {
    if (hasLoadedOnceRef.current) setRefreshing(true); else setLoading(true);
    const params = { sort };
    if (filters.category !== "all") params.category = filters.category;
    if (filters.karat !== "all") params.karat = filters.karat;
    if (filters.gemstone !== "all") params.gemstone = filters.gemstone;
    if (filters.in_stock) params.in_stock = true;
    if (search) params.search = search;
    if (featuredOnly) params.featured = true;
    if (priceRange[0]) params.min_price = priceRange[0];
    if (priceRange[1]) params.max_price = priceRange[1];
    try {
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [sort, filters, priceRange, search, featuredOnly]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setPage(1); }, [filters, priceRange, sort, typeFilter, search, featuredOnly]);

  const onReset = () => {
    setFilters({ category: "all", karat: "all", gemstone: "all", in_stock: false });
    setPriceRange([facets.min_price || 0, facets.max_price || 1000000]);
    setTypeFilter("All");
    setSearchParams({});
  };

  // Rebuild an equivalent search string with exactly one recognised concept removed —
  // re-joining every OTHER canonical concept value + the leftover free-text words. Re-parsing
  // canonical words (e.g. "Rings", "18K", "Rose Gold", "Party") is idempotent, so this stays
  // robust across repeated chip removals instead of trying to surgically edit the shopper's
  // original phrasing.
  const removeSearchConcept = (field, value) => {
    const c = searchConcepts;
    const tokens = [];
    (field === "category" ? c.category.filter((v) => v !== value) : c.category).forEach((v) => tokens.push(v));
    if (field === "karat" ? false : c.karat) tokens.push(c.karat);
    (field === "gold_color" ? c.gold_color.filter((v) => v !== value) : c.gold_color).forEach((v) => tokens.push(`${v} Gold`));
    if (field === "diamond_type" ? false : c.diamond_type) tokens.push(c.diamond_type);
    (field === "diamond_shape" ? c.diamond_shape.filter((v) => v !== value) : c.diamond_shape).forEach((v) => tokens.push(v));
    (field === "gemstone" ? c.gemstone.filter((v) => v !== value) : c.gemstone).forEach((v) => tokens.push(v));
    (field === "style" ? c.style.filter((v) => v !== value) : c.style).forEach((v) => tokens.push(v));
    (field === "occasion" ? c.occasion.filter((v) => v !== value) : c.occasion).forEach((v) => tokens.push(v));
    (field === "jewellery_type_term" ? c.jewellery_type_term.filter((v) => v !== value) : c.jewellery_type_term).forEach((v) => tokens.push(v));
    (field === "leftover" ? [] : c.leftover_terms).forEach((v) => tokens.push(v));

    const next = tokens.join(" ").trim();
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set("search", next); else nextParams.delete("search");
    setSearchParams(nextParams);
  };

  // Unified chip list: manual sidebar-filter selections (category/karat/gemstone/in-stock/
  // price) PLUS every concept the Smart Search parser recognised in the free-text query —
  // each independently removable, shown together right below the page title.
  const filterChips = useMemo(() => {
    const chips = [];
    const seenLabels = new Set();
    const push = (testid, label, onRemove) => {
      if (seenLabels.has(label)) return; // avoid showing the same concept twice (e.g. category set both via URL param and via search text)
      seenLabels.add(label);
      chips.push({ key: testid, testid, label, onRemove });
    };

    if (filters.category !== "all") {
      push("plp-chip-category", filters.category, () => { setFilters((f) => ({ ...f, category: "all" })); setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("category"); return n; }); });
    }
    if (filters.karat !== "all") push("plp-chip-karat", filters.karat, () => setFilters((f) => ({ ...f, karat: "all" })));
    if (filters.gemstone !== "all") push("plp-chip-gemstone", filters.gemstone, () => setFilters((f) => ({ ...f, gemstone: "all" })));
    if (filters.in_stock) push("plp-chip-instock", "In Stock", () => setFilters((f) => ({ ...f, in_stock: false })));
    if (facets.min_price != null && (priceRange[0] > facets.min_price || priceRange[1] < facets.max_price)) {
      push("plp-chip-price", `${formatINR(priceRange[0])} – ${formatINR(priceRange[1])}`, () => setPriceRange([facets.min_price, facets.max_price]));
    }

    searchConcepts.category.forEach((v) => push(`plp-chip-search-category-${v}`, v, () => removeSearchConcept("category", v)));
    if (searchConcepts.karat) push("plp-chip-search-karat", searchConcepts.karat, () => removeSearchConcept("karat"));
    searchConcepts.gold_color.forEach((v) => push(`plp-chip-search-color-${v}`, `${v} Gold`, () => removeSearchConcept("gold_color", v)));
    if (searchConcepts.diamond_type) push("plp-chip-search-dtype", searchConcepts.diamond_type === "lab" ? "Lab-Grown Diamond" : "Natural Diamond", () => removeSearchConcept("diamond_type"));
    searchConcepts.diamond_shape.forEach((v) => push(`plp-chip-search-shape-${v}`, v, () => removeSearchConcept("diamond_shape", v)));
    searchConcepts.gemstone.forEach((v) => push(`plp-chip-search-gem-${v}`, v, () => removeSearchConcept("gemstone", v)));
    searchConcepts.style.forEach((v) => push(`plp-chip-search-style-${v}`, v, () => removeSearchConcept("style", v)));
    searchConcepts.occasion.forEach((v) => push(`plp-chip-search-occasion-${v}`, v, () => removeSearchConcept("occasion", v)));
    searchConcepts.jewellery_type_term.forEach((v) => push(`plp-chip-search-jtype-${v}`, v, () => removeSearchConcept("jewellery_type_term", v)));
    if (searchConcepts.leftover_terms.length > 0) {
      push("plp-chip-search-leftover", `"${searchConcepts.leftover_terms.join(" ")}"`, () => removeSearchConcept("leftover"));
    }

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, priceRange, facets, searchConcepts]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.category !== "all") n++;
    if (filters.karat !== "all") n++;
    if (filters.gemstone !== "all") n++;
    if (filters.in_stock) n++;
    if (facets.min_price != null && (priceRange[0] > facets.min_price || priceRange[1] < facets.max_price)) n++;
    return n;
  }, [filters, priceRange, facets]);

  const typePills = filters.category !== "all" ? TYPE_FILTERS[filters.category] : null;
  const visibleProducts = useMemo(() => {
    if (!typePills || typeFilter === "All") return products;
    const kw = typeFilter.toLowerCase().replace(/s$/, "");
    return products.filter((p) => (p.name || "").toLowerCase().includes(kw));
  }, [products, typeFilter, typePills]);

  // Product search / "All Jewellery" results naturally span multiple categories —
  // group them with a heading per category instead of one flat mixed grid. A single
  // -category view (sidebar filter set, or a search that only matched one category)
  // has nothing to group, so it falls straight through to the normal flat/paginated
  // grid below — no redundant single heading, no behaviour change for that case.
  const toTitleCase = (s) => (s || "").toLowerCase().split(" ").map((w) => w ? w.charAt(0).toUpperCase() + w.slice(1) : w).join(" ");
  const GROUP_PREVIEW_COUNT = 8;
  const groupedByCategory = useMemo(() => {
    // Group by a normalised (trimmed, lower-cased) key first — some seeded categories
    // exist in more than one casing (e.g. "EARRINGS" vs "Earrings"), which would
    // otherwise show as two separate headings for what is really one category.
    const byKey = {};
    visibleProducts.forEach((p) => {
      const raw = p.category || "Other";
      const key = raw.trim().toLowerCase();
      if (!byKey[key]) byKey[key] = { label: toTitleCase(raw), items: [] };
      byKey[key].items.push(p);
    });
    // Prefer the admin-defined category name (categories API) for the heading, matched
    // case-insensitively, so the display label is always clean Title Case.
    categories.forEach((c) => {
      const key = (c.name || "").trim().toLowerCase();
      if (byKey[key]) byKey[key].label = toTitleCase(c.name);
    });
    const orderedKeys = [...new Set(categories.map((c) => (c.name || "").trim().toLowerCase()))].filter((k) => byKey[k]);
    const extraKeys = Object.keys(byKey).filter((k) => !orderedKeys.includes(k)).sort();
    return [...orderedKeys, ...extraKeys].map((key) => ({ key, name: byKey[key].label, items: byKey[key].items }));
  }, [visibleProducts, categories]);
  const isGrouped = groupedByCategory.length > 1;

  const totalPages = Math.max(1, Math.ceil(visibleProducts.length / PAGE_SIZE));
  const pageItems = visibleProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageTitle = search ? `Results for "${search}"` : featuredOnly ? "Bestsellers & Featured" : filters.category !== "all" ? filters.category : "All Jewellery";

  const gridCols = view === "grid"
    ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
    : "grid-cols-1 sm:grid-cols-2";

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, "e", totalPages];
    if (page >= totalPages - 2) return [1, "e", totalPages - 2, totalPages - 1, totalPages];
    return [1, "e", page, "e2", totalPages];
  }, [page, totalPages]);

  return (
    <StoreLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
        {/* Title + Filter button */}
        <div className="flex items-start justify-between mb-6 gap-3">
          <div className="min-w-0">
            <h1 data-testid="plp-title" className="font-display text-3xl sm:text-4xl font-medium tracking-tight break-words">{pageTitle}</h1>
          </div>
        </div>

        {/* Search Filter Chips — every recognised facet (manual sidebar filters + Smart
            Search concepts parsed from the free-text query) shown as a removable pill row,
            right alongside the search results. Horizontal scroll on narrow screens; never
            wraps, so it can't push the page wider than the viewport. */}
        {filterChips.length > 0 && (
          <div data-testid="plp-filter-chips-row" className="flex items-center gap-2 flex-nowrap overflow-x-auto scrollbar-none mb-6 -mx-1 px-1">
            {filterChips.map((chip) => (
              <FilterChip key={chip.key} testid={chip.testid} label={chip.label} onRemove={chip.onRemove} />
            ))}
            {filterChips.length > 1 && (
              <button
                type="button"
                data-testid="plp-filter-chips-clear-all"
                onClick={onReset}
                className="shrink-0 text-xs text-muted-foreground hover:text-[hsl(var(--primary))] underline underline-offset-2 whitespace-nowrap transition-colors duration-150 ml-1"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Type pills */}
        {typePills && (
          <div className="flex items-center gap-2 flex-wrap mb-6 overflow-x-auto scrollbar-none">
            {["All", ...typePills].map((pill) => (
              <button
                key={pill}
                data-testid={`plp-type-pill-${pill.toLowerCase()}`}
                onClick={() => setTypeFilter(pill)}
                className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-colors duration-200 ${
                  typeFilter === pill
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                    : "bg-card text-foreground/70 hover:text-[hsl(var(--primary))] card-shadow-soft"
                }`}
              >{pill}</button>
            ))}
          </div>
        )}

        {/* Filter + Sort + count + view toggle — Filter and Sort are now compact
            icon-only buttons (no text labels), so the whole row comfortably stays on
            one line at every breakpoint, down to the narrowest phones. */}
        <div className="flex items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet>
              <SheetTrigger asChild>
                <Button data-testid="plp-filter-open-button" variant="outline" aria-label="Filter products" className="h-10 rounded-xl bg-card gap-1.5 px-2.5 shrink-0">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  {activeCount > 0 && (
                    <span className="h-5 min-w-5 px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-semibold flex items-center justify-center">{activeCount}</span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto flex flex-col p-0">
                <SheetHeader className="px-5 py-4 border-b border-border flex-row items-center justify-between space-y-0">
                  <SheetTitle className="text-lg">Filter</SheetTitle>
                  <SheetClose asChild>
                    <Button data-testid="plp-filter-close-button" variant="ghost" size="icon" className="h-8 w-8"><X className="h-5 w-5" /></Button>
                  </SheetClose>
                </SheetHeader>
                <div className="flex-1 px-5">
                  <Filters facets={facets} categories={categories} filters={filters} setFilters={setFilters} priceRange={priceRange} setPriceRange={setPriceRange} />
                </div>
                <SheetFooter className="flex-row gap-3 p-4 border-t border-border">
                  <Button data-testid="plp-filter-clear-button" variant="outline" className="flex-1 rounded-xl h-12 text-[hsl(var(--destructive))] border-border hover:bg-[hsl(var(--destructive))]/5" onClick={onReset}>Clear all</Button>
                  <SheetClose asChild>
                    <Button data-testid="plp-filter-apply-button" className="flex-[1.4] rounded-xl h-12">Apply filters{activeCount > 0 ? ` (${activeCount})` : ""}</Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="plp-sort-trigger" aria-label="Sort products" className="h-10 w-10 p-0 justify-center gap-0 rounded-xl bg-card shrink-0">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span data-testid="plp-item-count" className="text-sm text-muted-foreground hidden sm:inline">{visibleProducts.length} Items</span>
            <div className="flex items-center rounded-lg overflow-hidden border border-border shrink-0">
              <button data-testid="plp-view-grid" onClick={() => setView("grid")} aria-label="Grid view"
                className={`h-9 w-9 flex items-center justify-center transition-colors ${view === "grid" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-card text-muted-foreground hover:text-foreground"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button data-testid="plp-view-list" onClick={() => setView("list")} aria-label="List view"
                className={`h-9 w-9 flex items-center justify-center transition-colors ${view === "list" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-card text-muted-foreground hover:text-foreground"}`}><List className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* Grid — grouped by category (with a heading per section) whenever results span
            more than one category, e.g. "All Jewellery" browsing or a multi-category
            search. A single-category view falls through to the original flat/paginated
            grid unchanged. */}
        {loading ? (
          <div className={`grid ${gridCols} gap-x-5 gap-y-8`}>{[...Array(8)].map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-[hsl(var(--sand))] animate-pulse" />)}</div>
        ) : visibleProducts.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground">
            <X className="h-10 w-10 mx-auto mb-3 opacity-40" />
            No products match your filters.
          </div>
        ) : isGrouped ? (
          <div className="space-y-12" data-testid="plp-grouped-results">
            {groupedByCategory.map((group) => (
              <div key={group.key} data-testid={`plp-category-group-${group.name}`}>
                <div className="flex items-end justify-between mb-5 gap-3">
                  <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">{group.name}</h2>
                  <span className="text-sm text-muted-foreground shrink-0">{group.items.length} piece{group.items.length === 1 ? "" : "s"}</span>
                </div>
                <motion.div
                  variants={container}
                  initial="hidden"
                  animate="show"
                  className={`grid ${gridCols} gap-x-5 gap-y-8 sm:gap-x-6 sm:gap-y-10 transition-opacity duration-200 ${refreshing ? "opacity-60" : "opacity-100"}`}
                >
                  {group.items.slice(0, GROUP_PREVIEW_COUNT).map((p) => <ProductCard key={p.id} product={p} />)}
                </motion.div>
                {group.items.length > GROUP_PREVIEW_COUNT && (
                  <div className="mt-5">
                    <Link
                      to={`/products?category=${encodeURIComponent(group.name)}`}
                      data-testid={`plp-view-all-${group.name}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] hover:underline underline-offset-2 transition-colors duration-150"
                    >
                      View all {group.items.length} in {group.name} <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              data-testid="plp-products-grid"
              className={`grid ${gridCols} gap-x-5 gap-y-8 sm:gap-x-6 sm:gap-y-10 transition-opacity duration-200 ${refreshing ? "opacity-60" : "opacity-100"}`}
            >
              {pageItems.map((p) => <ProductCard key={p.id} product={p} />)}
            </motion.div>

            {totalPages > 1 && (
              <Pagination className="mt-12">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious data-testid="plp-page-prev" href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }} className={page === 1 ? "pointer-events-none opacity-40" : ""} />
                  </PaginationItem>
                  {pageNumbers.map((n, idx) => (
                    <PaginationItem key={`page-${n}-${idx}`}>
                      {typeof n === "string" ? <PaginationEllipsis /> : (
                        <PaginationLink data-testid={`plp-page-${n}`} href="#" isActive={n === page} onClick={(e) => { e.preventDefault(); setPage(n); }}>{n}</PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext data-testid="plp-page-next" href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(totalPages, p + 1)); }} className={page === totalPages ? "pointer-events-none opacity-40" : ""} />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </div>
    </StoreLayout>
  );
}
